/**
 * cobroQuickHelpers
 * -----------------
 * Helpers puros (sin React) usados por `CobroQuickDialog` para emparejar
 * pacientes y normalizar datos del formulario.
 *
 * Extraídos en Phase QR2 para bajar la complejidad del componente principal.
 * No tienen estado y son fácilmente testeables.
 */
import { fullName } from './api';
import { isDevolucionConcept } from './cobroPresets';
import { desgloseIvaIncluido } from './iva';

export const PAYMENT_METHODS = ['Tarjeta', 'Transferencia', 'Enlace de pago', 'Efectivo', 'Bizum', 'Financiación'];

/** Empareja un paciente por nombre con prioridades: exacto > inclusión > primera palabra. */
export function findPatientMatch(patients, name) {
  if (!name || !patients?.length) return null;
  const target = name.toLowerCase().trim();

  // 1) Coincidencia exacta (case-insensitive)
  const exact = patients.find(p => fullName(p).toLowerCase() === target);
  if (exact) return exact;

  // 2) Contención bidireccional
  const partial = patients.find(p => {
    const f = fullName(p).toLowerCase();
    return f && (f.includes(target) || target.includes(f));
  });
  if (partial) return partial;

  // 3) Por primera palabra significativa (≥3 caracteres)
  const firstWord = target.split(/\s+/)[0];
  if (firstWord && firstWord.length >= 3) {
    const byFirst = patients.find(p => (p.name || '').toLowerCase().startsWith(firstWord));
    if (byFirst) return byFirst;
  }
  return null;
}

/** Construye una dirección legible para el campo del formulario. */
export function patientAddressLine(patient) {
  if (!patient?.address) return '';
  const a = patient.address;
  return [a.calle, a.poblacion, a.provincia, a.codigo_postal].filter(Boolean).join(', ');
}

/**
 * parseAmountFromNote (Phase 23.10)
 * ---------------------------------
 * Extrae un importe sugerido y/o método de pago de un texto libre (la
 * `clinical_note` de una cita de agenda donde recepción o enfermería suelen anotar "150€ tarjeta",
 * "Cobro 80 efectivo", "Total 250,50 €", etc.).
 *
 * Devuelve `{ amount, paymentMethod }`. Cualquier campo no detectado se
 * devuelve como null/'' para que el caller pueda usar sus propios defaults.
 *
 * Patrones soportados (en orden de prioridad):
 *  - "150€" "150 €" "150,50€" "150.50€"
 *  - "150 EUR" "150 eur" "EUR 150"
 *  - "cobro 80" "total 250" "abonar 100" (con keyword)
 *  - Métodos: tarjeta, transferencia, bizum, efectivo, enlace, financiación
 */
const _PAYMENT_PATTERNS = [
  { re: /\btarjeta\b/i, value: 'Tarjeta' },
  { re: /\b(transferencia|transfer)\b/i, value: 'Transferencia' },
  { re: /\bbizum\b/i, value: 'Bizum' },
  { re: /\b(efectivo|cash|metalico|metálico)\b/i, value: 'Efectivo' },
  { re: /\b(enlace|link\s*de\s*pago|payment\s*link)\b/i, value: 'Enlace de pago' },
  { re: /\b(financiac|financiación)/i, value: 'Financiación' },
];

const _AMOUNT_PATTERNS = [
  // 150€ · 150 € · 150,50€ · 150.50 € · 1.500€
  /(\d{1,3}(?:\.\d{3})+|\d+)[\s\u00a0]*(?:[,.](\d{1,2}))?[\s\u00a0]*€/i,
  // 150 EUR · EUR 150 · 150 eur
  /(\d{1,3}(?:\.\d{3})+|\d+)[\s\u00a0]*(?:[,.](\d{1,2}))?[\s\u00a0]*(?:eur|euros?)\b/i,
  // "cobro 80" · "total 250" · "abonar 100"
  /\b(?:cobro|total|abon[ao]r?|importe|precio|paga[r]?)\s*[:\-]?\s*(\d{1,3}(?:\.\d{3})+|\d+)(?:[,.](\d{1,2}))?\b/i,
];

export function parseAmountFromNote(text) {
  if (!text || typeof text !== 'string') return { amount: null, paymentMethod: '' };
  let amount = null;
  for (const re of _AMOUNT_PATTERNS) {
    const m = text.match(re);
    if (m) {
      // m[1] es el entero (puede tener miles "1.500"), m[2] son los decimales
      const intPart = m[1].replace(/\./g, '');  // quitar separador de miles
      const decPart = m[2] ? `.${m[2]}` : '';
      const parsed = parseFloat(intPart + decPart);
      if (!Number.isNaN(parsed) && parsed > 0 && parsed < 100000) {
        amount = parsed;
        break;
      }
    }
  }
  let paymentMethod = '';
  for (const p of _PAYMENT_PATTERNS) {
    if (p.re.test(text)) {
      paymentMethod = p.value;
      break;
    }
  }
  return { amount, paymentMethod };
}

/** Construye el estado inicial del formulario a partir de un paciente (opcional) y datos prefill. */
export function buildInitialForm({
  patient,
  prefillName = '',
  defaultConcept = '',
  defaultAmount = null,
  defaultPaymentMethod = '',
}) {
  return {
    patient_id: patient?.id || '',
    patient_name: patient ? fullName(patient) : (prefillName || ''),
    nif: patient?.nif || '',
    address: patientAddressLine(patient),
    concept: defaultConcept || 'Consulta',
    amount: defaultAmount != null ? String(defaultAmount) : '',
    vat_applied: false,
    // Sin método por defecto (2026-08-31): hay que elegirlo a mano.
    payment_method: defaultPaymentMethod || '',
    date: new Date().toISOString().slice(0, 10),
    notes: '',
  };
}

/** Valida el formulario antes de enviar. Devuelve mensaje de error o null si todo OK. */
export function validateCobroForm(form) {
  if (!form.patient_name) return 'Indica el nombre del paciente';
  if (!form.concept) return 'Indica el concepto';
  if (!form.amount || Number(form.amount) <= 0) return 'Indica un importe válido';
  if (!form.payment_method) return 'Elige el método de pago';
  return null;
}

/**
 * Importe con el signo que le toca (2026-09-08): la persona escribe
 * la cantidad en positivo y, si el concepto es una devolución, se manda en
 * negativo. El backend lo garantiza igualmente.
 */
export function signedCobroAmount(amount, concept) {
  const abs = Math.abs(Number(amount || 0));
  return isDevolucionConcept(concept) ? -abs : abs;
}

/**
 * Base imponible, IVA y total de lo que se está apuntando
 * (24/9/2026): el importe escrito es lo que paga el paciente y, con IVA, el
 * 21% sale de DENTRO (100 → 82,64 + 17,36 = 100). Hasta ese día se sumaba
 * encima y 100 acababa en 121. Misma cuenta que el servidor (lib/iva.js).
 * Una devolución va en negativo y sin IVA.
 */
export function computeCobroDesglose(amount, vatApplied, concept = '') {
  if (isDevolucionConcept(concept)) return desgloseIvaIncluido(signedCobroAmount(amount, concept), false);
  return desgloseIvaIncluido(amount, !!vatApplied);
}

/** El total que se cobra: el importe escrito (con el IVA dentro si lo lleva). */
export function computeCobroTotal(amount, vatApplied, concept = '') {
  return computeCobroDesglose(amount, vatApplied, concept).total;
}
